/**
 * Bringt customer_dashboard_meta in jedem Fall auf ein Array.
 *
 * Das Feld ist AWSJSON und wird von zwei Seiten geschrieben. Über die
 * updateSpace-Mutation deserialisiert AppSync den String und legt eine native
 * Liste ab — sauber. Schreibt eine Lambda per DynamoDB-SDK dagegen einen String
 * hinein, kommt beim Lesen ein String zurück, und ein einmaliges JSON.parse
 * ergibt wieder einen String. Genau das hat auf staging den Login eines
 * eingeladenen Nutzers mit „t.some is not a function" abgebrochen.
 *
 * Der schreibende Fehler ist behoben, aber die Ursache lag nicht hier: ein
 * einzelner krummer Wert im Space durfte nie die gesamte App blockieren. Also
 * hier zusätzlich abfangen — auch für Altdaten, die schon so in der DB liegen.
 */
export const normalizeMeta = (raw) => {
  let value = raw
  // Bis zu zweimal auflösen: einfach oder doppelt stringifiziert.
  for (let i = 0; i < 2 && typeof value === 'string'; i++) {
    try {
      value = JSON.parse(value)
    } catch {
      return []
    }
  }
  if (Array.isArray(value)) return value
  // Einzelnes Objekt (kommt bei Career-Metas vor) → als ein Eintrag behandeln.
  if (value && typeof value === 'object') return [value]
  return []
}

// Einzige Definition von „Onboarding ist erledigt", weil die Prüfung an zwei
// Stellen gebraucht wird: hier für den UserContext und in App.js, das den Space
// vor dem Context-Aufbau selbst parsen muss. Vorher standen beide Varianten
// getrennt da und hätten bei jeder Erweiterung auseinanderlaufen können.
const ONBOARDING_DONE_STATES = ['COMPLETED', 'SKIPPED']

export const hasOnboardingDone = (meta) =>
  normalizeMeta(meta).some(
    (item) =>
      item?.event === 'ONBOARDING' &&
      ONBOARDING_DONE_STATES.includes(String(item?.status || '').toUpperCase())
  )

export const extractCustomerDashboardMeta = ({ customer_dashboard_meta }) => {
  customer_dashboard_meta = normalizeMeta(customer_dashboard_meta)
  const has_seen_product_tour = customer_dashboard_meta.some(
    ({ event, status }) => event === 'PRODUCT_TOUR' && status === 'FINISHED'
  )

  // COMPLETED = selbst durchlaufen, SKIPPED = nie nötig gewesen (in einen
  // eingerichteten Account eingeladen). Fürs Gate gleichwertig, in den Daten
  // getrennt — siehe hasOnboardingDone oben.
  const hasCompletedOnboarding = hasOnboardingDone(customer_dashboard_meta)

  const has_seen_favored_space_hint = customer_dashboard_meta.some(
    ({ event, status }) => event === 'FAVORED_SPACE_HINT' && status === 'SEEN'
  )

  const has_seen_store_partner_logo_hint = customer_dashboard_meta.some(
    ({ event, status }) => event === 'STORE_PARTNER_LOGO' && status === 'SEEN'
  )

  const ui_settings_general = customer_dashboard_meta.find(
    ({ id }) => id === 'UI_GENERAL'
  ) || { id: 'UI_GENERAL' }

  const ui_space_state_hint = customer_dashboard_meta.find(
    ({ id }) => id === 'UI_SPACE_STATE_HINT'
  ) || { id: 'UI_SPACE_STATE_HINT' }

  const hasSeenOrSubmittedMatchingProfileFeedback =
    customer_dashboard_meta.some(
      ({ event, status }) =>
        event === 'FEEDBACK_MATCHINGPROFILE' &&
        (status === 'SEEN' || status === 'SUBMITTED')
    )

  const hasSeenOrSubmittedTalentProfileFeedback = customer_dashboard_meta.some(
    ({ event, status }) =>
      event === 'FEEDBACK_TALENTPROFILE' &&
      (status === 'SEEN' || status === 'SUBMITTED')
  )

  const hasSeenOrSubmittedRequirementProfileFeedback =
    customer_dashboard_meta.some(
      ({ event, status }) =>
        event === 'FEEDBACK_REQUIREMENTPROFILE' &&
        (status === 'SEEN' || status === 'SUBMITTED')
    )

  const hasSeenHelpSupportHint = customer_dashboard_meta.some(
    ({ event, status }) =>
      event === 'SHOW_HELP_SUPPORT_HINT' && status === 'SEEN'
  )

  return {
    has_seen_product_tour,
    has_seen_favored_space_hint,
    hasCompletedOnboarding,
    has_seen_store_partner_logo_hint,
    hasSeenOrSubmittedMatchingProfileFeedback,
    hasSeenOrSubmittedTalentProfileFeedback,
    hasSeenOrSubmittedRequirementProfileFeedback,
    hasSeenHelpSupportHint,
    ui_settings_general,
    ui_space_state_hint
  }
}
